import { post, get } from '@/utils';
import { ApiResponse } from '@/utils';
import {
  ImageTaskParams,
  VideoTaskParams,
  TaskQueryParams,
  TaskCreateResponse,
  TaskQueryResponse,
  FeedListResponse,
  FeedListParams,
  CreditCalculationResponse,
  FeatureSwitchItem,
} from '../types';

/**
 * AMAI内容生成服务
 * 用于处理图像和视频生成任务
 */
class GenerationService {
  /**
   * 创建图片生成任务
   * @param params 任务参数 {user_id, prompt, model_id_list, creative_mode?, image_ratio?, number?, ref_images?, ref_type?}
   * @returns 任务创建结果，包含任务ID (msg_iid)
   */
  async createImageTask(params: ImageTaskParams): Promise<ApiResponse<TaskCreateResponse>> {
    return post<TaskCreateResponse>('/image/task', params);
  }

  /**
   * 计算图片生成任务所需点数
   * @param params 任务参数，与创建任务的参数一致
   * @returns 计算结果，包含总消耗点数和用户剩余点数
   */
  async calculateImageTaskCredits(params: ImageTaskParams): Promise<CreditCalculationResponse> {
    return post('/image/task/credits', params) as any;
  }

  /**
   * 查询图片生成任务结果
   * @param params 查询参数 {user_id, msg_iid}
   * @returns 任务结果，包含生成的图片资源
   */
  async queryImageTask(params: TaskQueryParams): Promise<ApiResponse<TaskQueryResponse>> {
    return post<TaskQueryResponse>('/image/query', params);
  }

  /**
   * 创建视频生成任务
   * @param params 任务参数 {user_id, prompt, model_id_list, tail_image, duration?, number?, ref_images?}
   * @returns 任务创建结果，包含任务ID (msg_iid)
   */
  async createVideoTask(params: VideoTaskParams): Promise<ApiResponse<TaskCreateResponse>> {
    return post<TaskCreateResponse>('/video/task', params);
  }

  /**
   * 计算视频生成任务所需点数
   * @param params 任务参数，与创建任务的参数一致
   * @returns 计算结果，包含总消耗点数和用户剩余点数
   */
  async calculateVideoTaskCredits(params: VideoTaskParams): Promise<CreditCalculationResponse> {
    return post('/video/task/credits', params) as any;
  }

  /**
   * 查询视频生成任务结果
   * @param params 查询参数 {user_id, msg_iid}
   * @param token 可选的访问令牌
   * @returns 任务结果，包含生成的视频资源
   */
  async queryVideoTask(params: TaskQueryParams): Promise<ApiResponse<TaskQueryResponse>> {
    return post<TaskQueryResponse>('/video/query', params);
  }

  /**
   * 获取图片和视频消息列表(Feed流)
   * @param params 查询参数 { creation_type_list?, page_number?, page_size?, msg_id_list? }
   * @returns 图片和视频消息列表
   */
  async getFeedList(params: FeedListParams): Promise<ApiResponse<FeedListResponse>> {
    return post<FeedListResponse>('/feed/list', params);
  }

  /**
   * 获取功能开关配置
   * @returns 功能开关状态对象，如 { "SD2.5-1080P-72off": { status, begin_time, end_time } }
   */
  async getFeatureSwitch(): Promise<ApiResponse<Record<string, FeatureSwitchItem>>> {
    return get<Record<string, FeatureSwitchItem>>('/feature/switch');
  }
}

export default new GenerationService();
